/* ============================================================
   EqualFuel motion bootstrap.
   - Content is fully usable with zero JS (progressive enhancement).
   - Adds html.ef-motion only when motion is on AND the user does
     not prefer reduced motion (WCAG).
   - Cheap primitives (reveal, count-up, SVG draw) run on
     IntersectionObserver + CSS transitions — no library.
   - Heavy primitives (smooth scroll, pinned scrub chapters) load
     vendored Lenis/GSAP on demand, only at intensity "cinematic".
   ============================================================ */
(function () {
  'use strict';

  var S = (window.EF && window.EF.settings) || {};
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionOn = S.motion !== false && !reduced;

  window.EF = window.EF || {};
  window.EF.motionActive = motionOn;

  if (!motionOn) return;

  document.documentElement.classList.add('ef-motion');

  /* ---------- script loader for vendored libs ---------- */
  var loaded = {};
  function loadScript(file) {
    if (loaded[file]) return loaded[file];
    loaded[file] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = (S.assetBase || '/') + file;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return loaded[file];
  }
  window.EF.loadScript = loadScript;

  var gsapReady = null;
  function ensureGsap() {
    if (!gsapReady) {
      gsapReady = loadScript('ef-vendor-gsap.js')
        .then(function () { return loadScript('ef-vendor-scrolltrigger.js'); })
        .then(function () {
          window.gsap.registerPlugin(window.ScrollTrigger);
          return window.gsap;
        });
    }
    return gsapReady;
  }
  window.EF.ensureGsap = ensureGsap;

  /* ---------- reveal (CSS transition, IO one-shot) ---------- */
  function initReveals(root) {
    var els = (root || document).querySelectorAll('[data-ef-reveal]:not(.ef-revealed)');
    if (!els.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          /* pin timelines own their elements at cinematic intensity —
             .ef-revealed's !important would fight GSAP's inline styles */
          if (S.intensity === 'cinematic' && el.closest('[data-ef-pin]')) {
            io.unobserve(el);
            return;
          }
          var idx = parseInt(el.getAttribute('data-ef-reveal'), 10);
          if (!isNaN(idx)) el.style.setProperty('--ef-reveal-delay', idx * 70 + 'ms');
          el.classList.add('ef-revealed');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- count-up numerals ---------- */
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-ef-countup'));
    if (isNaN(target)) return;
    var decimals = (el.getAttribute('data-ef-countup').split('.')[1] || '').length;
    var duration = 1400;
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (target * eased).toFixed(decimals);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = el.getAttribute('data-ef-countup');
    }
    requestAnimationFrame(step);
  }
  function initCountups(root) {
    var els = (root || document).querySelectorAll('[data-ef-countup]:not(.ef-counted)');
    if (!els.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('ef-counted');
          animateCount(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- SVG line draw ---------- */
  function initDraws(root) {
    var els = (root || document).querySelectorAll('[data-ef-draw]:not(.ef-drawn)');
    if (!els.length) return;
    els.forEach(function (el) {
      try {
        var len = el.getTotalLength ? Math.ceil(el.getTotalLength()) : 1600;
        el.style.setProperty('--ef-draw-length', len);
      } catch (e) { /* non-path elements keep default */ }
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('ef-drawn');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px' }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- pinned scrub chapters (cinematic only) ---------- */
  function initPins(root) {
    var pins = (root || document).querySelectorAll('[data-ef-pin]:not([data-ef-pinned])');
    if (!pins.length || S.intensity !== 'cinematic') return;
    pins.forEach(function (pin) { pin.setAttribute('data-ef-pinned', ''); });
    ensureGsap().then(function (gsap) {
      pins.forEach(function (pin) {
        var steps = pin.querySelectorAll('[data-ef-pin-step]');
        var visual = pin.querySelector('[data-ef-pin-visual]');
        if (!steps.length) return;
        var tl = gsap.timeline({
          scrollTrigger: {
            trigger: pin,
            start: 'top top',
            end: '+=' + steps.length * 80 + '%',
            pin: true,
            scrub: 0.6,
            anticipatePin: 1
          }
        });
        steps.forEach(function (stepEl, i) {
          if (i > 0) {
            tl.to(steps[i - 1], { autoAlpha: 0, y: -30, duration: 0.4 }, i);
            tl.fromTo(stepEl, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.4 }, i + 0.1);
          } else {
            gsap.set(stepEl, { autoAlpha: 1 });
          }
        });
        if (visual) {
          tl.fromTo(visual, { scale: 0.92 }, { scale: 1, duration: steps.length, ease: 'none' }, 0);
        }
      });
    });
  }

  /* ---------- smooth scroll (opt-in, cinematic, desktop only) ----------
     Wrapping native scroll is the single riskiest motion feature (a
     stalled raf loop = frozen page), so it ships OFF and is enabled
     explicitly via Theme settings → EqualFuel → Smooth scroll. */
  function initLenis() {
    if (S.smooth !== true) return;
    if (S.intensity !== 'cinematic') return;
    if (window.innerWidth < 1024 || 'ontouchstart' in window) return;
    loadScript('ef-vendor-lenis.js').then(function () {
      ensureGsap().then(function (gsap) {
        var lenis = new window.Lenis({ lerp: 0.11 });
        lenis.on('scroll', window.ScrollTrigger.update);
        gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
        gsap.ticker.lagSmoothing(0);
        window.EF.lenis = lenis;
      });
    });
  }

  /* ---------- floating parallax layers ----------
     [data-ef-float="0.15"] drifts vertically at the given speed
     relative to scroll (transform-only, rAF-throttled, IO-gated). */
  var floatEls = [];
  var floatRaf = null;
  function renderFloats() {
    floatRaf = null;
    var vh = window.innerHeight;
    floatEls.forEach(function (item) {
      if (!item.visible) return;
      var rect = item.el.getBoundingClientRect();
      var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      item.el.style.transform = 'translate3d(0,' + (-progress * item.speed * 100).toFixed(2) + 'px,0)';
    });
  }
  function onFloatScroll() {
    if (!floatRaf) floatRaf = requestAnimationFrame(renderFloats);
  }
  function initFloats(root) {
    var els = (root || document).querySelectorAll('[data-ef-float]:not([data-ef-floating])');
    if (!els.length) return;
    var started = floatEls.length > 0;
    els.forEach(function (el) {
      el.setAttribute('data-ef-floating', '');
      var item = { el: el, speed: parseFloat(el.getAttribute('data-ef-float')) || 0.15, visible: false };
      floatEls.push(item);
      var io = new IntersectionObserver(function (entries) {
        item.visible = entries[0].isIntersecting;
        if (item.visible) onFloatScroll();
      }, { rootMargin: '120px' });
      io.observe(el);
    });
    if (!started && floatEls.length) {
      window.addEventListener('scroll', onFloatScroll, { passive: true });
      window.addEventListener('resize', onFloatScroll, { passive: true });
    }
    onFloatScroll();
  }

  function initAll(root) {
    initReveals(root);
    initCountups(root);
    initDraws(root);
    initFloats(root);
  }

  /* Background-tab guard: layout measured in a hidden/zero-width tab is
     garbage (every ScrollTrigger pin would freeze the page). Defer the
     entire bootstrap until the document is visible with a real viewport,
     and re-measure whenever visibility or size changes. */
  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    initAll(document);
    initPins();
    initLenis();
  }
  function whenViewportReady() {
    /* Defer ONLY on true zero-width loads (background prerender where
       layout is garbage). Hidden-but-rendering tabs lay out at real
       width — measurements there are valid. */
    if (window.innerWidth > 0) {
      boot();
      return;
    }
    var tries = 0;
    var poll = setInterval(function () {
      if (window.innerWidth > 0 || ++tries > 40) {
        clearInterval(poll);
        if (window.innerWidth > 0) requestAnimationFrame(boot);
      }
    }, 500);
    document.addEventListener('visibilitychange', function onV() {
      if (window.innerWidth > 0) {
        document.removeEventListener('visibilitychange', onV);
        clearInterval(poll);
        requestAnimationFrame(boot);
      }
    });
  }

  /* Safety net — scheduled UNCONDITIONALLY: if IntersectionObserver
     callbacks cannot run (hidden/prerender/background-load states render
     pixels while reporting visibilityState "hidden", and timers +
     transitions are throttled), content must never stay invisible. The
     CSS safety rule snaps opacity with transition:none so it resolves
     even while the render loop is frozen. Stagger is cosmetic; content
     is not. */
  function forceRevealIfStuck() {
    var targets = document.querySelectorAll('[data-ef-reveal]');
    if (!targets.length) return;
    var revealed = document.querySelectorAll('.ef-revealed');
    if (revealed.length < targets.length * 0.6) {
      document.documentElement.classList.add('ef-reveal-all');
    }
  }
  setTimeout(forceRevealIfStuck, 1200);

  /* Prerender / bfcache activation + tab foregrounding: the page may have
     been painted (and its reveal transitions frozen) while hidden. On the
     first real visibility, re-observe and force-reveal anything stuck so
     the user never lands on an empty section. */
  function onBecomeVisible() {
    if (document.visibilityState !== 'visible') return;
    initReveals(document);
    forceRevealIfStuck();
    if (window.ScrollTrigger) requestAnimationFrame(function () { window.ScrollTrigger.refresh(); });
  }
  document.addEventListener('visibilitychange', onBecomeVisible);
  window.addEventListener('pageshow', function (e) { if (e.persisted) onBecomeVisible(); });
  var resizeT;
  window.addEventListener('resize', function () {
    clearTimeout(resizeT);
    resizeT = setTimeout(function () {
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, 200);
  }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', whenViewportReady);
  } else {
    whenViewportReady();
  }

  /* Re-init inside the theme editor when sections re-render */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    initPins(e.target);
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });

  window.EF.refresh = function () {
    initAll(document);
    initPins(document);
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  };
})();
